/**
 * ViewRegistry.js
 * ─────────────────────────────────────────────────────────
 * Maps every componentName (stored in localStorage) to its
 * lazy-loaded React component.
 *
 * To add a new view:
 *   1. Add one line to REGISTRY below.
 *   2. That's it — no changes needed anywhere else.
 *
 * Usage (in your app entry point):
 *   import { renderView } from "./ViewRegistry";
 *   const View = renderView();   // reads localStorage automatically
 *   ReactDOM.render(<View />, document.getElementById("root"));
 * ─────────────────────────────────────────────────────────
 */
import React, { lazy, Suspense } from "react";
import Wait from "Jarvis/JarvisComponents/Wait";
import { getJarvisView } from "Jarvis/JarvisServices/JarvisLocalStorageService";

/* ── Registry — add new views here ───────────────────── */
const REGISTRY = {
    View1: lazy(() => import("Jarvis/JarvisApp/View1/index")),
    View2: lazy(() => import("Jarvis/JarvisApp/View2/index")),
    View3: lazy(() => import("Jarvis/JarvisApp/View3/index")),
    View4: lazy(() => import("Jarvis/JarvisApp/View4/index")),
};

/* Default view — used when localStorage is empty or key is unknown */
const DEFAULT_VIEW = "View3";

/**
 * getViewComponent()
 * Returns the lazy component for the currently selected view.
 * Falls back to DEFAULT_VIEW if the stored key isn't in the registry.
 */
export function getViewComponent() {
    const stored = getJarvisView().componentName;
    const key = (stored && REGISTRY[stored]) ? stored : DEFAULT_VIEW;
    return REGISTRY[key] ?? REGISTRY[DEFAULT_VIEW];
}

/**
 * renderView()
 * Convenience wrapper — returns a <Suspense>-wrapped component
 * ready to drop directly into ReactDOM.render / your router.
 *
 *   const View = renderView();
 *   return <View />;
 */
const _LazyView  = getViewComponent();
export function ViewRegistry(props) {
    return (
        <Suspense fallback={<Wait />}>
            <_LazyView {...props} />
        </Suspense>
    );
}